import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "./app/hooks";
import { useGetSessionStatusQuery, useLoginSsoMutation } from "./app/services/api";
import {createHref, RoutingSummary, selectRouting, useRouting} from "./app/services/routing";
import { RootState } from "./app/store";
import { buildApplicationUrl, DEFAULT_TARGET_APPLICATION, TargetApplicationID, TARGET_APPLICATIONS } from "./config";
import Loading from "./views/Loading";
import Login from "./views/Login";
import Logout from "./views/Logout";
import Redirecting from "./views/Redirecting";
import SSOError from "./views/SSOError";
import PasswordRecovery from "./views/PasswordRecovery";
import PageNotFoundError from "./views/PageNotFoundError";
import UnexpectedError from "./views/UnexpectedError";
import LogoLayout from "./layouts/LogoLayout";
import ResetPassword from "./views/ResetPassword";
import InviteTokenBox from "./components/InviteTokenBox/InviteTokenBox";


export default function App() {
    const routing = useRouting();
    const application = useAppSelector(selectApplicationFromRouting);
    const provider = useAppSelector(selectProviderFromRouting);
    const path = useAppSelector(selectPathFromRouting);
    const sessionStatus = useGetSessionStatusQuery();

    const [ssoRedirect, setSsoRedirect] = useState<{ provider: string; url: string }>();
    const [loginSso, loginSsoResult] = useLoginSsoMutation();

    const userHasActiveSession = sessionStatus.isSuccess && sessionStatus.data !== null;
    const userHasNoSession = sessionStatus.isSuccess && sessionStatus.data === null;

    const handleSSOProviderSelection = useCallback(
        async (provider: string) => {
            if (application === null) {
                throw new Error("No application has been selected yet.");
            }

            const result = await loginSso({
                provider,
                successUrl: buildApplicationUrl(application.url, path),
                failureUrl: createHref("error", {}, { absolute: true }),
            });

            if ("data" in result) {
                setSsoRedirect({ provider, url: result.data.url });
            }
        },
        [application, path]
    );

    useEffect(() => {
        if (application && provider && userHasNoSession) {
            // when an SSO provider has been explicitly passed in the URL, we short-circuit
            // the entire login process to redirect directly to the specified provider
            handleSSOProviderSelection(provider);
        }
    }, [application, provider, userHasNoSession, handleSSOProviderSelection]);

    let main = <LogoLayout shortLogo={true} footerContent={<Loading/>} flexDirection={'column'}/>;

    if (application) {
        if (routing.error) {
            // routing (URL handling) failed
            if (routing.error.status === 404) {
                main = <LogoLayout dialogContent={<PageNotFoundError /> }/>;
            } else {
                main = <LogoLayout dialogContent={<UnexpectedError /> }/>;
            }
        } else if (loginSsoResult.isError) {
            // unable to resolve the SSO provider
            let description;

            if (provider === null) {
                description = "An unexpected error occurred during SSO login.";
            } else if ("status" in loginSsoResult.error && loginSsoResult.error.status === 400) {
                description = `Provider '${provider}' is unknown.`;
            } else {
                description = `An unexpected error occurred during SSO login with provider '${provider}'.`;
            }

            main = <LogoLayout dialogContent={<UnexpectedError description={description} /> }/>;
        } else {
            // append the optional `path` query param to the application URL
            const applicationUrl = buildApplicationUrl(application.url, path);

            main = handleLoginSsoResultSuccess(routing, ssoRedirect, userHasActiveSession, userHasNoSession, provider, application.name, applicationUrl);
        }
    }


    return (
        <Box className="App" position="absolute" top="0" left="0" right="0" bottom="0">
            {main}
        </Box>
    );
}

function selectApplicationFromRouting(state: RootState) {
    const { ready, match } = selectRouting(state);

    if (!ready || !match) {
        return null;
    }

    if (match.route.id !== "application-login") {
        return TARGET_APPLICATIONS[DEFAULT_TARGET_APPLICATION];
    }

    const { application: applicationID } = match.params;

    if (applicationID && Object.hasOwn(TARGET_APPLICATIONS, applicationID)) {
        return TARGET_APPLICATIONS[applicationID as TargetApplicationID];
    }

    return TARGET_APPLICATIONS[DEFAULT_TARGET_APPLICATION];
}

function selectProviderFromRouting(state: RootState): string | null {
    const { location } = state.routing;

    if (location === null) {
        return null;
    }

    const search = new URLSearchParams(location.search);

    return search.get("provider");
}

function selectPathFromRouting(state: RootState): string | null {
    const { location } = state.routing;

    if (location === null) {
        return null;
    }

    const search = new URLSearchParams(location.search);

    return search.get("path");
}

function handleLoginSsoResultSuccess(routing: RoutingSummary,
    ssoRedirect: { provider: string; url: string } | undefined,
    userHasActiveSession: boolean,
    userHasNoSession: boolean,
    provider: string | null,
    applicationName: string,
    applicationUrl: string
) {
    let main = <></>;


    if (routing.match?.route.id === "logout") {
        // visitor is asking for the logout page/ui
        main =<LogoLayout dialogContent={<Logout /> }/>;
    } else if (routing.match?.route.id === "error") {
        // visitor is asking for the SSO failure page/ui
        main = <LogoLayout dialogContent={<SSOError /> }/>;
    } else if (routing.match?.route.id === "forgot-password") {
        // visitor is asking for password recovery ui
        main = <LogoLayout dialogContent={<PasswordRecovery /> }/>;
    } else if (routing.match?.route.id === "reset-password") {
        // visitor is asking for password recovery completion ui
        main = <LogoLayout dialogContent={<ResetPassword /> }/>;
    } else if (routing.match?.route.id === "invite") {
        // visitor is asking for token based onboarding
        main = <LogoLayout dialogContent={<InviteTokenBox />} />;
    } else if (routing.match?.route.id === "redirect") {
        const {next} = routing.match.params;

        // Visitor has succeeded access code verified.
        main = <LogoLayout
            footerContent={<Redirecting icon="unlocked"  url={String(next)} />}
            flexDirection={'column'}
        />;
    } else if (ssoRedirect) {
        // we need to redirect the user to an SSO provider
        main = <LogoLayout
            footerContent={<Redirecting icon="locked" name={ssoRedirect.provider} url={ssoRedirect.url} />}
            flexDirection={'column'}
        />;
    } else if (userHasActiveSession) {
        // user has an active session, so we directly forward them to the app
        // site
        main = <LogoLayout
            footerContent={<Redirecting icon="unlocked" name={applicationName} url={applicationUrl} /> }
            flexDirection={'column'}
        />;
    } else if (userHasNoSession && provider === null) {
        // user has no active session and no SSO provider, so show login UI
        main = <LogoLayout dialogContent={<Login />}/>;
    }

    return main;
}
